import classNames from 'classnames'
import { DragNDropFileUploader } from 'legacy/lut/library/dragNDrop'
import React, { useMemo } from 'react'

import { Accordion } from 'core-components/library/accordion'
import {
  IDragNDropFileUploaderProps,
  IFileSettings,
} from 'core-components/library/dragNDropFileUploader'
import { TCheckFn, Validator } from 'core-components/library/validation'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'

import { IFileMeta, TDeleteFileMeta } from './types'

import styles from './FilePanel.module.scss'

export interface IFilePanelProps {
  value: IFileMeta[]
  onUpload: (items: IFileSettings[]) => void

  onDelete?: (item: TDeleteFileMeta) => void
  audienceSettings?: IAudienceCondition
  disabled?: boolean
  isMultiFiles?: boolean
  acceptedFormats?: string
  required?: boolean
  checkFn?: TCheckFn
  // todo: is it deprecated?
  validatorField?: string
  isReadOnly?: boolean
}

export const FilePanel = ({
  value,
  audienceSettings,
  onDelete,
  onUpload,
  disabled,
  isMultiFiles,
  acceptedFormats,
  required,
  checkFn,
  validatorField,
  isReadOnly,
}: IFilePanelProps) => {
  const fileName = useMemo(() => {
    return value.map((i) => i.name)
  }, [value])

  const fileNamesAsString = useMemo(() => {
    return fileName.join(', ')
  }, [fileName])

  const uploaded = useMemo(() => value.filter((i) => Boolean(i.link) && Boolean(i.uid)), [value])
  const toBeUploaded = useMemo(() => value.filter((i) => !i.link), [value])
  const isAnyUploaded = Boolean(uploaded.length)
  const isAnyToBeUploaded = Boolean(toBeUploaded.length)

  const fileUploaderProps: IDragNDropFileUploaderProps = {
    fileNames: fileName,
    required,
    multifiles: isMultiFiles,
    urlInputVisibility: false,
    fileListVisibility: false,
    changeFileLoader: onUpload,
    acceptedFormats,
    disabled,
  }
  const isAbleToDelete = !(disabled || isReadOnly)

  const dragAndDropToRender = audienceSettings ? (
    <VisualAudienceExtended<IDragNDropFileUploaderProps>
      write={audienceSettings}
      component={DragNDropFileUploader}
      {...fileUploaderProps}
    />
  ) : (
    <DragNDropFileUploader {...fileUploaderProps} />
  )

  const panelToRender = (
    <div>
      {!isReadOnly && dragAndDropToRender}

      {(isAnyUploaded || isAnyToBeUploaded) && (
        <div className={styles.wrapper}>
          {isAnyUploaded && (
            <Accordion title={'Uploaded'} opened={true}>
              <FileList items={uploaded} onDelete={isAbleToDelete ? onDelete : undefined} />
            </Accordion>
          )}
          {isAnyToBeUploaded && (
            <Accordion title={'To Be Uploaded'} opened={true}>
              <FileList items={toBeUploaded} onDelete={isAbleToDelete ? onDelete : undefined} />
            </Accordion>
          )}
        </div>
      )}
    </div>
  )

  return required && checkFn ? (
    <Validator checkFn={checkFn} value={fileNamesAsString} field={validatorField}>
      {panelToRender}
    </Validator>
  ) : (
    panelToRender
  )
}

interface IFileListProps {
  items: IFileMeta[]
  onDelete: IFilePanelProps['onDelete']
}

const FileList = ({ items, onDelete }: IFileListProps) => {
  return (
    <div className={styles.listContainer}>
      {items.map(({ link, name, entryId, uid }) => {
        return (
          <div key={`${entryId}-${name}-${uid || ''}`} className={styles.listItem}>
            {link ? (
              <a
                href={link}
                rel={'noreferrer'}
                target={'_blank'}
                title={name}
                className={styles.link}
              >
                {name}
              </a>
            ) : (
              <span className={styles.link} title={name}>
                {name}
              </span>
            )}

            {onDelete ? (
              <div
                className={classNames(styles.trashButton, 'kbase kbase-delete kbase-sm')}
                onClick={() => onDelete({ entryId, uid, name })}
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
